import { FC } from "react";
import { Controller, FieldPath, useFormContext } from "react-hook-form";
import {
  TextField,
  FormControlLabel,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import { IAbsenceRequestFormValues } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  ICustomFieldDTO,
  FieldType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface IAbsenceRequestCustomFieldsProps {
  customFields: ICustomFieldDTO[];
}

const AbsenceRequestCustomFields: FC<IAbsenceRequestCustomFieldsProps> = ({
  customFields = [],
}) => {
  const { control } = useFormContext();

  return (
    <>
      {customFields.map((field) => {
        const {
          customFieldId,
          name,
          fieldType,
          description,
          label,
          required,
          choices,
          multipleChoice,
        } = field;
        const id = `customField_${customFieldId}`;

        return (
          <Fieldset
            key={id}
            legend={name}
            description={description}
            required={required}
          >
            {fieldType === FieldType.TEXT && (
              <Controller
                name={id as FieldPath<IAbsenceRequestFormValues>}
                control={control}
                render={({
                  field: { ref, ...fieldProps },
                  fieldState: { invalid, error },
                }) => (
                  <TextField
                    {...fieldProps}
                    id={id}
                    label={name}
                    inputRef={ref}
                    error={invalid}
                    helperText={error?.message}
                    fullWidth
                    required={required}
                    data-testid={`absence-request-form-field__${id}`}
                  />
                )}
              />
            )}

            {fieldType === FieldType.DATE && (
              <Controller
                name={id as FieldPath<IAbsenceRequestFormValues>}
                control={control}
                render={({
                  field: { value, onChange, ...fieldProps },
                  fieldState: { invalid, error },
                }) => (
                  <DatePicker
                    {...fieldProps}
                    value={value}
                    onChange={onChange}
                    label={name}
                    dayOfWeekFormatter={(date: Date) =>
                      date.toLocaleString("pl", { weekday: "short" })
                    }
                    slotProps={{
                      textField: {
                        id,
                        required,
                        fullWidth: true,
                        error: invalid,
                        helperText: error?.message,
                      },
                    }}
                    data-testid={`absence-request-form-field__${id}`}
                  />
                )}
              />
            )}

            {fieldType === FieldType.CHECKBOX && (
              <Controller
                name={id as FieldPath<IAbsenceRequestFormValues>}
                control={control}
                render={({
                  field: { value, ...fieldProps },
                  fieldState: { invalid, error },
                }) => (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...fieldProps}
                          checked={!!value}
                          data-testid={`absence-request-form-field__${id}`}
                        />
                      }
                      label={label}
                    />
                    {invalid && error?.message && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            )}

            {fieldType === FieldType.CHOICE_SELECTION && (
              <Controller
                name={id as FieldPath<IAbsenceRequestFormValues>}
                control={control}
                render={({
                  field: { ref, ...fieldProps },
                  fieldState: { invalid, error },
                }) => (
                  <FormControl fullWidth error={invalid} required={required}>
                    <InputLabel id={`${id}-label`}>{name}</InputLabel>
                    <Select
                      {...fieldProps}
                      labelId={`${id}-label`}
                      id={id}
                      label={name}
                      inputRef={ref}
                      multiple={multipleChoice}
                      data-testid={`absence-request-form-field__${id}`}
                    >
                      {choices?.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {invalid && error?.message && (
                      <FormHelperText>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}
          </Fieldset>
        );
      })}
    </>
  );
};

export default AbsenceRequestCustomFields;
